import { forwardRef, useCallback, useMemo, useState } from "react";

import { cn } from "@btnet/utilities-core";

import {
    BTLogoDeprecated,
    type IBTLogoDeprecatedProps,
} from "commonComponents/utilities/BTLogo/BTLogoDeprecated";

import "./ImageFullResolutionDangerous.less";

export interface IImageFullResolutionDangerousProps {
    /** if undefined/null fallback will be immediately rendered */
    src: string | undefined | null;

    /**
     * The FULL IMAGE will be loaded form the server, then the browser will resize it on the client.
     * Set "getFromServerDangerous" if you MUST.
     * @see https://www.smashingmagazine.com/2020/03/setting-height-width-images-important-again/
     */
    renderedWidth: number | "getFromServerDangerous";

    /**
     * The FULL IMAGE will be loaded form the server, then the browser will resize it on the client.
     * Set "getFromServerDangerous" if you MUST.
     * @see https://www.smashingmagazine.com/2020/03/setting-height-width-images-important-again/
     */
    renderedHeight: number | "getFromServerDangerous";

    alt: string;
    "data-testid"?: string;

    /** class for element wrapping the img */
    wrapperClassName?: string;
    /** class for the img element */
    className?: string;

    style?: React.CSSProperties;

    /** content to render when the image is loading, or not found */
    fallback?: React.ReactNode;

    onLoad?: React.ReactEventHandler<HTMLImageElement>;
    onError?: React.ReactEventHandler<HTMLImageElement>;
    /** If true, the image will not be loaded until it is in the viewport
     * @default true
     */
    lazyLoad?: boolean;
}

/*
const throwIfContainsSize = (src: string | undefined | null) => {
    if (src === undefined || src === null) {
        return;
    }

    const qsParams = queryString.parseUrl(src);

    if (qsParams.query["width"] || qsParams.query["height"]) {
        throw new Error(
            `ImageFullResolutionDangerous URL has width and height already specified. Use props instead. You passed - ${src}`
        );
    }
};
*/

function getFallbackLogoSize(logoSize: number): IBTLogoDeprecatedProps["size"] {
    if (logoSize < 50) {
        return "sm";
    }
    if (logoSize < 100) {
        return "md";
    }
    if (logoSize < 150) {
        return "lg";
    }

    return "xl";
}

const emptyFuncDefault = () => {};
/**
 * @deprecated  not actually deprecated, but please use `<Thumbnail>` if possible.
 *
 * The `ImageFullResolutionDangerous` component is a utility component that requests a
 * FULL SIZED image from GetFile.ashx
 * The image is then resized on the client to fit the `renderedWidth` and `renderedHeight`.
 *
 * You should attempt to use the `<Thumbnail>` component before using this component.
 *
 * Use this component only when you absolutely must have the full size image from the server.
 *
 * This component:
 * - Loads significantly slower on clients browsers. Use `<Thumbnail>` instead!
 * - Causes server performance problems. Use `<Thumbnail>` instead!
 * - Costs buildertrend more money due to bandwidth/server costs. Use `<Thumbnail>` instead!
 */
export const ImageFullResolutionDangerous = forwardRef<
    HTMLImageElement,
    IImageFullResolutionDangerousProps
>(
    (
        {
            src,
            renderedWidth,
            renderedHeight,
            alt,
            onLoad = emptyFuncDefault,
            onError = emptyFuncDefault,
            fallback,
            wrapperClassName,
            className,
            style,
            "data-testid": dataTestId,
            lazyLoad = true,
        },
        ref,
    ) => {
        // temp disabling until we can fix PDF generation issue https://dev.azure.com/buildertrend/BuildertrendProjects/_workitems/edit/97004
        // throwIfContainsSize(src);

        const [useFallback, setUseFallback] = useState<boolean>(false);
        const srcEmpty = src === undefined || src === null;

        const widthPixels = renderedWidth === "getFromServerDangerous" ? undefined : renderedWidth;
        const heightPixels =
            renderedHeight === "getFromServerDangerous" ? undefined : renderedHeight;

        const photoStyles = useMemo(() => {
            const imageStyle: React.CSSProperties | undefined = {
                maxHeight: `${heightPixels}px`,
                maxWidth: `${widthPixels}px`,
                ...style,
            };

            return imageStyle;
        }, [style, widthPixels, heightPixels]);

        const handleLoad = useCallback(
            (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
                onLoad(event);
            },
            [onLoad],
        );

        const handleError = useCallback(
            (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
                onError(event);
                setUseFallback(true);
            },
            [onError],
        );

        const fallbackSize = Math.min(widthPixels ?? 100, heightPixels ?? 100);
        const fallbackLogoSize = getFallbackLogoSize(fallbackSize);

        if (useFallback || srcEmpty) {
            return (
                <div
                    className={cn(
                        "ImageFullResolutionDangerous",
                        "ImageFullResolutionDangerous--fallback",
                    )}
                    style={photoStyles}
                >
                    <div
                        className={cn(
                            "flex align-items-center",
                            {
                                "justify-content-center": className !== "left",
                            },
                            wrapperClassName,
                        )}
                    >
                        {fallback ? (
                            fallback
                        ) : (
                            <div
                                style={{
                                    width: fallbackSize,
                                    height: fallbackSize,
                                }}
                                className={cn("ImageFullResolutionDangerous--fallback", {
                                    "ImageFullResolutionDangerous--fallback-default": !fallback,
                                })}
                            >
                                <BTLogoDeprecated
                                    layout="minimal"
                                    color="black-grey"
                                    size={fallbackLogoSize}
                                    className="ImageFullResolutionDangerous--BTLogo-light"
                                />
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return (
            <img
                src={src}
                width={widthPixels}
                height={heightPixels}
                style={photoStyles}
                alt={alt}
                data-testid={dataTestId}
                className={cn("ImageFullResolutionDangerous", className)}
                loading={lazyLoad ? "lazy" : "eager"}
                onLoad={handleLoad}
                onError={handleError}
                ref={ref}
            />
        );
    },
);
